import type { MedplumClient } from '@medplum/core';
import type { Practitioner } from '@medplum/fhirtypes';
import {
  formatPractitionerPreferredLabel,
  formatPractitionerStaffLabel,
} from '@unify/fhir-model/resources/practitioner';

import { EXTENSION_URLS } from '../../features/rbac/constants';
import { PROVIDER_ROLES, type Role } from '../../pages/users/types/rbac';
import { organizationIdsFromPractitioner } from '../adminDirectoryMapping';

export interface FacilitatorOption {
  value: string;
  label: string;
  preferredLabel?: string;
  practiceId: string;
}

function isClinicalRole(p: Practitioner): boolean {
  const role = p.extension?.find((e) => e.url === EXTENSION_URLS.role)?.valueString;
  // Missing role extension defaults to provider, matching practitionerToUser in the admin directory.
  if (!role) return true;
  return PROVIDER_ROLES.includes(role as Role);
}

/**
 * Clinicians selectable as group facilitators, one entry per practice they belong to.
 *
 * Practice membership comes from the `unify.health/fhir/organization` extension **or** the
 * Organization compartment, so practitioners linked either way are selectable.
 */
export async function listGroupFacilitatorOptions(
  medplum: MedplumClient,
): Promise<FacilitatorOption[]> {
  const practitioners = await medplum.searchResources('Practitioner', { _count: '500' });

  const options: FacilitatorOption[] = [];
  for (const p of practitioners) {
    if (!p.id || p.active === false || !isClinicalRole(p)) continue;
    const label = formatPractitionerStaffLabel(p) || 'Unknown';
    const preferredLabel = formatPractitionerPreferredLabel(p) || undefined;
    for (const practiceId of organizationIdsFromPractitioner(p)) {
      options.push({ value: p.id, label, preferredLabel, practiceId });
    }
  }
  return options.sort((a, b) => a.label.localeCompare(b.label));
}
